/* ============================================================================
 * milliDashboardData.js — data and helpers for Milli's dashboard (085).
 *
 * One round trip: `kc_dashboard()` is an owner-gated RPC (migration 085) that
 * returns every block the front door shows (funnel, pipeline, channels, demos,
 * activation, units, next moves, search, calls, phone, approvals). A non-owner
 * gets an error, never someone else's numbers. Nothing here writes.
 *
 * The four brand marks are the public files on kocoresystems.com, the same
 * canonical set the design department composes with. `plate` marks a file
 * that carries its own background (rounded corners in the UI).
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

export const fetchDashboard = async () => {
  const { data, error } = await supabase.rpc('kc_dashboard');
  if (error) throw error;
  return data || {};
};

export const BRAND = 'https://kocoresystems.com/brand';

export const CORE4 = [
  { key: 'core', name: 'K.O. CO+RE Systems', src: `${BRAND}/ko_core_lockup_transparent_v1.png` },
  { key: 'pq', name: 'PQ Intelligence', src: `${BRAND}/pq_intelligence_wordmark_transparent_copper-steel_v1.png` },
  { key: 'construction', name: 'K.O. Construction-Remodeling', src: `${BRAND}/ko_construction_primary.jpg`, plate: true },
  { key: 'milli', name: 'Milli', src: `${BRAND}/milli_wordmark_script_transparent_v1.webp` },
];

export const MILLI_WORDMARK = `${BRAND}/milli_wordmark_script_transparent_v1.webp`;

/** FormQuick.io's lockup (rulebook v1.5, migration 098): black plate, so it sits on the card's dark ground as is. */
export const FORMQUICK_LOCKUP = `${BRAND}/formquick_lockup_dark_v1.png`;

/** "+3", "-2" or null when either side is missing or nothing moved. */
export const delta = (now, prev) => {
  const a = Number(now);
  const b = Number(prev);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const d = a - b;
  if (d === 0) return null;
  return d > 0 ? `+${d}` : `${d}`;
};

/** Grouped integer, or null when the value is not a number (so callers can
 * show "connect ..." or "--" instead of a made-up zero). */
export const fmtNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
};

/** Cents to "$1,234" (whole dollars; cents shown only when not whole), or
 * null when the value is not a number, so the card can say "--". */
export const fmtMoney = (cents, currency = 'usd') => {
  if (cents === null || cents === undefined || cents === '') return null;
  const n = Number(cents);
  if (!Number.isFinite(n)) return null;
  const whole = n % 100 === 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
  }).format(n / 100);
};

/** "Sep 30" or "Sep 30, 2:00 PM" in Central time. Empty string when unset. */
export const fmtWhen = (value, { withTime = false } = {}) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { month: 'short', day: 'numeric', timeZone: 'America/Chicago' };
  if (withTime) {
    opts.hour = 'numeric';
    opts.minute = '2-digit';
  }
  return new Intl.DateTimeFormat('en-US', opts).format(d);
};

/** Whole days since a timestamp, 0 for today, null when unset. */
export const daysAgo = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
